import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import {
  decode, validate, prettyDecode, inspectBytes,
  EVENT_CATEGORIES, SEVERITY_LEVELS, STATUS_CODES, RADIUS_CODES, RESPONSE_FLAGS,
} from '@astdz/codec';
import { apiGet, apiPost } from '../lib/api';
import { CopyButton } from '../components/CopyButton';
import { Feedback } from '../components/Feedback';
import { FamilyConsole } from './FamilyConsole';

type Tab = 'encode' | 'decode' | 'search' | 'family';

interface EncodeResp {
  hex: string;
  bytes: number;
  tier: number;
  prettyOutput: string;
  teaser?: string;
}

interface MessageItem {
  refCode: number;
  refHex: string;
  title: string;
  fullText: string;
  recommendedSeverity: number | null;
}

interface MessagesResp {
  messages: MessageItem[];
  total: number;
}

const TIER_NAMES: Record<number, string> = { 0: 'NANO', 1: 'MICRO', 2: 'FULL' };

function parseId(value: string, max: number): number | null {
  const normalized = value.trim();
  if (!/^(?:0x[0-9a-f]+|[0-9]+)$/i.test(normalized)) return null;
  const number = Number(normalized);
  return Number.isInteger(number) && number >= 0 && number <= max ? number : null;
}

/** Route: `/dashboard/console`. Encode/decode/search playground for EMRG payloads. */
export function ConsolePage() {
  const [tab, setTab] = useState<Tab>('encode');

  // Encode form state
  const [category, setCategory] = useState('03');
  const [severity, setSeverity] = useState(3);
  const [status, setStatus] = useState('01');
  const [radius, setRadius] = useState(1);
  const [tier, setTier] = useState<'' | '0' | '1' | '2'>('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [flags, setFlags] = useState(0);
  const [refHex, setRefHex] = useState('');
  const [refTitle, setRefTitle] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const [deviceId, setDeviceId] = useState('1');
  const [encodeResult, setEncodeResult] = useState<EncodeResp | null>(null);
  const [encodeError, setEncodeError] = useState<string | null>(null);

  // Decode tab state
  const [decodeHex, setDecodeHex] = useState('');
  const [decodeResult, setDecodeResult] = useState<ReturnType<typeof decode> | null>(null);

  // Search tab state
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MessagesResp | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Debounce search
  useEffect(() => {
    if (tab !== 'search') return;
    let active = true;
    setSearchError(null);
    setResults(null);
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      params.set('limit', '20');
      params.set('offset', String(offset));
      apiGet<MessagesResp>(`/v1/messages?${params.toString()}`)
        .then((response) => { if (active) setResults(response); })
        .catch((err: Error) => { if (active) setSearchError(err.message || 'Unable to search messages.'); });
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [tab, query, offset]);

  function toggleFlag(bit: number) {
    setFlags((f) => f ^ (1 << bit));
  }

  async function lookupRef() {
    const num = parseId(refHex, 0xFFFF);
    if (num === null || num === 0) { setRefTitle(null); return; }
    try {
      const m = await apiGet<{ title: string; fullText: string }>(`/v1/messages/0x${num.toString(16)}`);
      setRefTitle(m.title);
      if (!freeText) setFreeText(m.fullText);
    } catch {
      setRefTitle(null);
    }
  }

  async function handleEncode(e: FormEvent) {
    e.preventDefault();
    setEncodeError(null);
    setEncodeResult(null);
    const refNum = refHex.trim() ? parseId(refHex, 0xFFFF) : 0;
    const devNum = parseId(deviceId, 0xFFFFFF);
    if (refNum === null || devNum === null) {
      setEncodeError('Enter a complete hexadecimal or decimal ID: message reference up to 0xFFFF and device ID up to 0xFFFFFF.');
      return;
    }
    const body: Record<string, unknown> = {
      schema: 'EMRG',
      eventCategory: category,
      severity,
      status,
      radius,
      messageRef: refNum,
      freeText,
      deviceId: devNum,
      responseFlags: flags,
    };
    if (tier !== '') body.tier = Number(tier);
    if (lat.trim() || lng.trim()) {
      const latitude = Number(lat);
      const longitude = Number(lng);
      if (!lat.trim() || !lng.trim() || !Number.isFinite(latitude) || !Number.isFinite(longitude)
        || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
        setEncodeError('Enter both coordinates: latitude from -90 to 90 and longitude from -180 to 180.');
        return;
      }
      body.lat = latitude;
      body.lng = longitude;
    }
    try {
      const res = await apiPost<EncodeResp>('/v1/encode', body);
      setEncodeResult(res);
    } catch (err) {
      setEncodeError(err instanceof Error ? err.message : 'encode failed');
      setEncodeResult(null);
    }
  }

  function handleDecode() {
    const v = validate(decodeHex);
    if (!v.valid) {
      setDecodeResult({ valid: false, errors: v.errors, tier: v.tier as 0 | 1 | 2, payload: decode(decodeHex).payload });
      return;
    }
    setDecodeResult(decode(decodeHex));
  }

  const inspectFields = useMemo(() => {
    if (!decodeResult || !decodeHex) return [];
    return inspectBytes(decodeHex);
  }, [decodeResult, decodeHex]);

  function pickMessage(m: MessageItem) {
    setRefHex('0x' + m.refCode.toString(16).padStart(4, '0'));
    setRefTitle(m.title);
    setFreeText(m.fullText);
    if (m.recommendedSeverity) setSeverity(m.recommendedSeverity);
    setTab('encode');
  }

  return (
    <div>
      <div className="tabs">
        <button className={tab === 'encode' ? 'active' : ''} onClick={() => setTab('encode')}>[encode]</button>
        <button className={tab === 'decode' ? 'active' : ''} onClick={() => setTab('decode')}>[decode]</button>
        <button className={tab === 'search' ? 'active' : ''} onClick={() => setTab('search')}>[search]</button>
        <button className={tab === 'family' ? 'active' : ''} onClick={() => setTab('family')}>[family codecs]</button>
      </div>

      {tab === 'encode' && (
        <div className="split">
          <form onSubmit={handleEncode}>
            <div className="text-muted">── event ─────────────────────────────────────────────────</div>
            <div className="grid-2">
              <div className="form-row">
                <label className="label">schema</label>
                <div>EMRG™</div>
              </div>
              <div className="form-row">
                <label className="label" htmlFor="console-tier">tier</label>
                <select id="console-tier" className="form-input" value={tier} onChange={(e) => setTier(e.target.value as '' | '0' | '1' | '2')}>
                  <option value="">auto</option>
                  <option value="0">0 — NANO</option>
                  <option value="1">1 — MICRO</option>
                  <option value="2">2 — FULL</option>
                </select>
              </div>
              <div className="form-row">
                <label className="label" htmlFor="console-category">category</label>
                <select id="console-category" className="form-input" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {Object.entries(EVENT_CATEGORIES).map(([code, name]) => (
                    <option key={code} value={code}>{code} {name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label className="label" htmlFor="console-severity">severity</label>
                <select id="console-severity" className="form-input" value={severity} onChange={(e) => setSeverity(Number(e.target.value))}>
                  {Object.entries(SEVERITY_LEVELS).map(([n, s]) => (
                    <option key={n} value={n}>{n} {s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label className="label" htmlFor="console-status">status</label>
                <select id="console-status" className="form-input" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {Object.entries(STATUS_CODES).map(([code, name]) => (
                    <option key={code} value={code}>{code} {name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label className="label" htmlFor="console-radius">radius</label>
                <select id="console-radius" className="form-input" value={radius} onChange={(e) => setRadius(Number(e.target.value))}>
                  {Object.entries(RADIUS_CODES).map(([code, r]) => (
                    <option key={code} value={code}>{code} {r.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="text-muted">── location ──────────────────────────────────────────────</div>
            <div className="grid-2">
              <div className="form-row">
                <label className="label" htmlFor="console-lat">lat</label>
                <input id="console-lat" className="form-input" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="39.978371" />
              </div>
              <div className="form-row">
                <label className="label" htmlFor="console-lng">lng</label>
                <input id="console-lng" className="form-input" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="-86.118097" />
              </div>
            </div>

            <div className="text-muted">── response flags ────────────────────────────────────────</div>
            <div className="grid-2" style={{ marginBottom: 16 }}>
              {[15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4].map((bit) => (
                <label key={bit}>
                  <input type="checkbox" checked={(flags & (1 << bit)) !== 0} onChange={() => toggleFlag(bit)} />
                  {' '}{RESPONSE_FLAGS[bit]}
                </label>
              ))}
            </div>

            <div className="text-muted">── message ───────────────────────────────────────────────</div>
            <div className="form-row">
              <label className="label" htmlFor="console-refHex">ref (hex)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input id="console-refHex" className="form-input" value={refHex} onChange={(e) => setRefHex(e.target.value)} onBlur={lookupRef} placeholder="0x0001" />
                <button type="button" className="btn" onClick={lookupRef}>lookup</button>
              </div>
              {refTitle && <p className="form-hint">→ {refTitle}</p>}
            </div>
            <div className="form-row">
              <label className="label" htmlFor="console-freeText">free text</label>
              <textarea id="console-freeText" className="form-input" rows={3} value={freeText} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFreeText(e.target.value)} />
            </div>
            <div className="form-row">
              <label className="label" htmlFor="console-deviceId">device id</label>
              <input id="console-deviceId" className="form-input" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} placeholder="0x000001" />
            </div>
            <button type="submit" className="btn btn-primary">encode →</button>
          </form>

          <div>
            {encodeError && <Feedback type="error" message={encodeError} />}
            {encodeResult && (
              <>
                <div className="text-muted">── result ────────────────────────────────────────────────</div>
                <p className="text-success">✓ {encodeResult.bytes} bytes · TIER {encodeResult.tier} · {TIER_NAMES[encodeResult.tier]}</p>
                <pre className="hex-display">{encodeResult.hex}</pre>
                <div style={{ marginTop: 4 }}>
                  <CopyButton text={encodeResult.hex} label="copy hex" />
                </div>
                {encodeResult.teaser && (
                  <>
                    <div className="text-muted" style={{ marginTop: 16 }}>── text teaser (broadcast on primary channel) ───────────</div>
                    <pre className="hex-display">{encodeResult.teaser}</pre>
                    <div style={{ marginTop: 4 }}>
                      <CopyButton text={encodeResult.teaser} label="copy teaser" />
                    </div>
                  </>
                )}
                <div className="text-muted" style={{ marginTop: 16 }}>── decoded ───────────────────────────────────────────────</div>
                <pre style={{ whiteSpace: 'pre', overflowX: 'auto', fontSize: 'var(--font-size-sm)' }}>{encodeResult.prettyOutput}</pre>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'decode' && (
        <div>
          <p className="text-muted">paste hex payload:</p>
          <textarea
            aria-label="Hex payload"
            className="form-input"
            rows={4}
            value={decodeHex}
            onChange={(e) => setDecodeHex(e.target.value.replace(/\s+/g, ''))}
            placeholder="454d524731..."
          />
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn" onClick={handleDecode} style={{ marginRight: 8 }}>decode →</button>
            <button type="button" className="btn" onClick={() => { setDecodeHex(''); setDecodeResult(null); }}>clear</button>
          </div>

          {decodeResult && (
            <div style={{ marginTop: 16 }}>
              {decodeResult.valid ? (
                <p className="text-success">✓ valid · TIER {decodeResult.tier}</p>
              ) : (
                <p className="text-danger">✗ invalid: {decodeResult.errors.join(', ')}</p>
              )}
              <pre style={{ whiteSpace: 'pre', overflowX: 'auto', fontSize: 'var(--font-size-sm)' }}>{prettyDecode(decodeResult)}</pre>
              <div className="text-muted" style={{ marginTop: 16 }}>── byte layout ───────────────────────────────────────────</div>
              <table className="byte-table">
                <thead>
                  <tr><th>offset</th><th>bytes</th><th>block</th><th>field</th><th>value</th></tr>
                </thead>
                <tbody>
                  {inspectFields.map((f, i) => (
                    <tr key={i}>
                      <td>0x{f.offset.toString(16).padStart(2, '0').toUpperCase()}</td>
                      <td>{f.length}b</td>
                      <td>{f.block}</td>
                      <td>{f.name}</td>
                      <td>{f.decoded}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 8 }}>
                <CopyButton text={decodeHex} label="copy payload" />
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'search' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input aria-label="Search EMRG messages" className="form-input" value={query} onChange={(e) => { setQuery(e.target.value); setOffset(0); }} placeholder="search messages..." />
          </div>
          <hr className="divider" />
          {searchError ? <Feedback type="error" message={searchError} /> : !results ? <p className="text-muted">searching...</p> : results.messages.length === 0 ? (
            <p className="text-muted">no results</p>
          ) : (
            <>
              {results.messages.map((m) => (
                <div key={m.refCode} role="button" tabIndex={0} style={{ marginBottom: 12, cursor: 'pointer' }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickMessage(m); } }} onClick={() => pickMessage(m)}>
                  <div>
                    <span className="mono">{m.refHex}</span>{'  '}
                    <span>{m.title}</span>{'  '}
                    <span className="text-muted">sev {m.recommendedSeverity ?? '—'}</span>
                  </div>
                  <div className="text-muted" style={{ paddingLeft: 56 }}>{m.fullText}</div>
                </div>
              ))}
              <div className="text-muted" style={{ marginTop: 12 }}>
                [{offset + 1}-{Math.min(offset + results.messages.length, results.total)} of {results.total}]{' '}
                <button className="btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 20))}>← prev</button>{' '}
                <button className="btn" disabled={offset + 20 >= results.total} onClick={() => setOffset(offset + 20)}>next →</button>
              </div>
            </>
          )}
        </div>
      )}
      {tab === 'family' && <FamilyConsole />}
    </div>
  );
}
